import { useCallback, useState } from 'react';
import { IconDownload } from 'components/Icons';
import { ensureDomainHasSessionToken, extractHostedUrl, getDataSecurily, isClarifaiDomain, isUrlSecured } from 'utils/inputs';
import cogoToast from 'components/ToastMessage/ToastMessage';
import { useUnloadConfirmation } from 'hooks/useUnloadConfirmation';
import { Button } from 'components/ui_library/Button';

interface DownloadImageButtonProps {
  input: CF.API.Inputs.Input;
  useBase64?: boolean; // downloads Base64 image data instead of hosted image
}

export const DownloadImageButton: React.FC<DownloadImageButtonProps> = ({ input, useBase64 = false }) => {
  const [isImageBeingDownloaded, setIsImageBeingDownloaded] = useState(false);

  useUnloadConfirmation(isImageBeingDownloaded);

  const handleDownload = useCallback(async () => {
    if (!isImageBeingDownloaded) {
      setIsImageBeingDownloaded(true);
      cogoToast.info('Preparing your image for download. This may take a moment.', { hideAfter: 5 });

      try {
        let blob: Blob;
        const fileName = 'clarifai-image.png';
        // @ts-expect-error - 👇 TS doesn't know that this is actually ImageInputData and is defined
        const base64Data = input?.data?.image?.base64;

        if (useBase64 && base64Data) {
          // Handle Base64 Image
          const byteCharacters = atob(base64Data);
          const byteNumbers = Array.from({ length: byteCharacters.length }, (_, i) => byteCharacters.charCodeAt(i));
          for (let i = 0; i < byteCharacters.length; i += 1) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          blob = new Blob([byteArray], { type: 'image/png' });
        } else {
          // Handle Hosted Image
          const imageUrl = await extractHostedUrl(input, 'large');

          const _isUrlSecured = isUrlSecured(imageUrl);
          if (isClarifaiDomain() && _isUrlSecured) {
            ensureDomainHasSessionToken();
          }

          const response = await getDataSecurily(imageUrl);
          blob = await response.blob();
        }

        // Create a downloadable URL and trigger the download
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.download = fileName;
        document.body.append(link);
        link.click();

        // Cleanup
        link.remove();
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error downloading image:', error);
        cogoToast.error('Failed to download the image. Please try again.');
      } finally {
        setIsImageBeingDownloaded(false);
      }
    }
  }, [input, isImageBeingDownloaded, useBase64]);

  return (
    <Button size="md" variant="tertiary" onClick={handleDownload} disabled={isImageBeingDownloaded} RightIcon={IconDownload}>
      {isImageBeingDownloaded ? 'Downloading...' : 'Download Image'}
    </Button>
  );
};
